'use client';

import * as React from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import type { SxProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { Icon } from '@phosphor-icons/react/dist/lib/types';
import { Desktop as DesktopIcon } from '@phosphor-icons/react/dist/ssr/Desktop';
import { DeviceTablet as DeviceTabletIcon } from '@phosphor-icons/react/dist/ssr/DeviceTablet';
import { Phone as PhoneIcon } from '@phosphor-icons/react/dist/ssr/Phone';
import type { ApexOptions } from 'apexcharts';
import { Box, Divider, CircularProgress } from '@mui/material';

import { Chart } from '../../core/chart/chart';

const iconMapping = { Desktop: DesktopIcon, Tablet: DeviceTabletIcon, Phone: PhoneIcon } as Record<string, Icon>;

export interface TrafficProps {
  title: string;
  chartSeries: number[];
  labels: string[];
  sx?: SxProps;
  loading?: boolean;
}

export function Traffic({ title, chartSeries, labels, sx, loading = false }: TrafficProps): React.JSX.Element {
  const chartOptions = useChartOptions(labels);
  
  // Calculate percentages for display
  const total = chartSeries.reduce((sum, value) => sum + value, 0);
  const percentages = total > 0 
    ? chartSeries.map(value => Math.round((value / total) * 100)) 
    : chartSeries.map(() => 0);

  const noData = !loading && (chartSeries.length === 0 || total === 0);

  return (
    <Card sx={sx}>
      <CardHeader title={title} />
      <Divider />
      <CardContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={40} />
          </Box>
        ) : noData ? (
          <Box sx={{ height: 340, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, color: '#aaa', borderRadius: 2 }}>
            No Data Available
          </Box>
        ) : (
          <Box sx={{ height: '300px', position: 'relative' }}>
            <Chart height={300} options={chartOptions} series={percentages} type="donut" width="100%" />
          </Box>
        )}
      </CardContent>
      <Divider />
      <CardContent>
        {!loading && !noData && (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center', justifyContent: 'center' }}>
            {labels.map((label, index) => {
              const Icon = iconMapping[label];

              return (
                <Stack key={label} spacing={1} sx={{ alignItems: 'center' }}>
                  {Icon ? <Icon fontSize="var(--icon-fontSize-lg)" /> : null}
                  <Typography variant="caption">{label}</Typography>
                  <Typography color="text.secondary" variant="subtitle2">
                    {percentages[index]}%
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
}

function useChartOptions(labels: string[]): ApexOptions {
  const theme = useTheme();

  return {
    chart: { background: 'transparent' },
    colors: [theme.palette.success.main, theme.palette.warning.main, theme.palette.error.main],
    dataLabels: { enabled: false },
    labels,
    legend: { show: false },
    plotOptions: { pie: { expandOnClick: false } },
    states: { active: { filter: { type: 'none' } }, hover: { filter: { type: 'none' } } },
    stroke: { width: 0 },
    theme: { mode: theme.palette.mode },
    tooltip: { fillSeriesColor: false },
  };
}
